import { useState, useEffect } from 'preact/hooks';
import { getCanvasScale, onCanvasScaleChange } from '../../utils/uiScale';
import FoilCanvas from '../FoilCanvas';
import { isFoilFinish } from '../../utils/sorcery/foil';
import { foilZonesForCard } from '../../utils/sorcery/foilZones';
import { DEFAULT_FOIL_PARAMS } from '../../utils/sorcery/foilParams';
import { t } from '../../localization/t';

/**
 * Presentational hand-fan for the opening-hand simulator. Echoes the
 * in-game centre hand (`renderHand` in GameBoard.jsx) so the widget reads
 * as a snapshot of the real game, but owns its own layout: the board's
 * hand is all portrait cards on a uniform angular step, and this one has
 * to fan spells and 90deg-rotated sites together in a single arc.
 *
 * Strips away everything that would tie this to live game state:
 * no drag handlers, no hover retract, no inspector, no drag-to-reorder.
 * Clicking a card toggles its mulligan selection; the parent owns which
 * keys are selected.
 *
 * Sizing is fully derived from the hand it is given. Card width, overlap
 * and arc all fall out of the card count and the spell/site mix. So any
 * opener an avatar can deal (Magician's atlas-less hand, or an avatar that
 * draws more or fewer cards) lays out without gaps and without overflowing.
 */

const BAD = '#C85C4A';

// Portrait card aspect. Doubles as a rotated site's horizontal footprint
// in units of cardWidth: a site laid landscape is exactly cardHeight wide.
const CARD_ASPECT = 175 / 120;
// Widest the fan may spread, in design px on the 1600 canvas.
const MAX_FAN_SPAN = 1180;
// Floor on how much of a card the next one may cover. Below this the
// hand stops reading as individual cards, so oversized openers shrink
// the cards instead of overlapping further.
const MIN_OVERLAP = 0.3;
// Tilt of the outermost cards. The arc is deliberately shallow. This is
// a readable dashboard widget, not a table-top hand.
const HALF_ARC_DEG = 7;

// Every visual state lives here as a custom property rather than an inline
// style. The `transform`, `z-index`, `box-shadow` and `filter` properties
// are declared ONLY in this stylesheet. The component passes in the
// computed arc values (`--fan-tx/ty/rot/z`) as custom properties. That
// inversion is what lets `:hover` / `.is-selected` retarget them: a rule
// can't outrank an inline declaration of the same property, which is why
// the previous revision needed `!important` on every hover rule.
const FAN_CSS = `
  @keyframes hand-fan-deal-in {
    from {
      opacity: 0;
      transform: translateX(var(--fan-tx, 0px))
                 translateY(calc(var(--fan-ty, 0px) + 34px))
                 rotate(var(--fan-rot, 0deg));
    }
  }
  .hand-fan-card {
    z-index: var(--fan-z, 1);
    transform: translateX(var(--fan-tx, 0px))
               translateY(calc(var(--fan-ty, 0px) + var(--fan-lift, 0px)))
               rotate(var(--fan-rot, 0deg));
    transition: transform 240ms cubic-bezier(0.23, 1, 0.32, 1);
    animation: hand-fan-deal-in 420ms cubic-bezier(0.22, 1, 0.36, 1) backwards;
  }
  .hand-fan-inner {
    border: 1px solid var(--fan-ring, rgba(180,140,60,0.3));
    box-shadow: var(--fan-shadow, 0 1px 2px rgba(0,0,0,0.55), 0 10px 24px rgba(0,0,0,0.5));
    filter: var(--fan-filter, none);
    transition: box-shadow 180ms ease-out, border-color 180ms ease-out, filter 180ms ease-out;
  }
  /* The lift is applied before the rotate in the transform chain, so it
     reads as screen-space "up" for both the vertical spells and the
     90deg-rotated sites. */
  @media (hover: hover) and (pointer: fine) {
    .hand-fan-card:hover { z-index: 60; }
    .hand-fan-card:hover:not(.is-selected) { --fan-lift: -14px; }
    .hand-fan-card:hover:not(.is-selected) .hand-fan-inner {
      --fan-ring: rgba(232,196,116,0.85);
      --fan-shadow:
        inset 0 0 0 1px rgba(212,168,67,0.5),
        0 2px 6px rgba(0,0,0,0.5),
        0 20px 44px rgba(0,0,0,0.55),
        0 0 22px rgba(212,168,67,0.16);
      --fan-filter: brightness(1.07) saturate(1.04);
    }
  }
  .hand-fan-card:active:not(.is-selected) { --fan-lift: -7px; }
  /* Selected sinks INTO the hand rather than dimming in place. The card
     is going back to the deck, so it reads as receding. */
  .hand-fan-card.is-selected { --fan-lift: 12px; }
  .hand-fan-card.is-selected .hand-fan-inner {
    --fan-ring: rgba(200,92,74,0.75);
    --fan-shadow:
      inset 0 0 0 1px rgba(200,92,74,0.45),
      0 1px 2px rgba(0,0,0,0.6),
      0 6px 18px rgba(0,0,0,0.5);
    --fan-filter: grayscale(0.65) brightness(0.58);
  }
  /* Counter-rotate so the badge stays upright on rotated site cards. */
  .hand-fan-badge { transform: rotate(calc(-1 * var(--fan-rot, 0deg))); }
  @media (prefers-reduced-motion: reduce) {
    @keyframes hand-fan-deal-in {
      from { opacity: 0; }
    }
    .hand-fan-card { transition-duration: 1ms; }
  }
`;

function isSiteEntry(entry) {
  if (!entry) return false;
  if (entry.zone === 'atlas') return true;
  const cat = entry.card?._sorceryCategory;
  return cat === 'Site';
}

// Foil tilt from the card's own place in the arc rather than from the pointer.
// The fan is a static display, so a fixed light angle that sweeps across the
// hand reads better than six cards all catching the light identically. And it
// costs no per-frame work. `t` is the -1..+1 position along the fan.
function foilTiltFor(t) {
  return { mx: 50 + t * 34, my: 50, fromCenter: Math.min(1, Math.abs(t) * 0.6) };
}

function cardImage(entry) {
  return entry?.printing?.image_url || entry?.card?.printings?.[0]?.image_url || '';
}

/**
 * One card in the fan. Split out of the map purely so each card can own its
 * own `covered` flag: FoilCanvas fires `onCover(true)` when its first live
 * frame paints, and that is also the signal that it actually GOT a WebGL
 * context. Over the global context cap it renders an empty canvas and never
 * fires, so keying the <img> off this state is what makes the fallback to a
 * plain card image automatic rather than a blank slot.
 */
function FanCard({
  entry, imgUrl, isFoil, foilTilt, selected, badgeSize,
  cardWidth, cardHeight, fanTx, fanTy, fanRot, zIndex, animationDelay,
  onClick, onMouseEnter, onMouseLeave,
}) {
  const [covered, setCovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`hand-fan-card absolute block cursor-pointer${selected ? ' is-selected' : ''}`}
      style={{
        left: '50%',
        bottom: 0,
        width: cardWidth,
        height: cardHeight,
        transformOrigin: 'bottom center',
        '--fan-tx': fanTx,
        '--fan-ty': fanTy,
        '--fan-rot': fanRot,
        '--fan-z': String(zIndex),
        animationDelay,
      }}
      title={entry?.card?.name || ''}
    >
      <div
        className="hand-fan-inner relative w-full h-full overflow-hidden rounded-card"
        style={{ background: 'rgba(0,0,0,0.55)' }}
      >
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={entry.card?.name || ''}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
            style={{ opacity: covered ? 0 : 1 }}
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center text-center px-2"
            style={{ color: 'rgba(232, 213, 160, 0.9)', fontSize: '0.6875cqw' }}
          >
            {entry?.card?.name}
          </div>
        )}

        {/* Live WebGL foil, same per-card FoilCanvas the season-pass and
            pack-opening surfaces use. It sits INSIDE the fan's transformed
            card, so the arc rotation. Including the 90deg site flip. Is
            inherited for free and no `rotate90` is needed: the quad stays
            portrait like the texture, and CSS turns both together. */}
        {isFoil && imgUrl ? (
          <FoilCanvas
            imageUrl={imgUrl}
            tilt={foilTilt}
            scrollSweep={false}
            zones={foilZonesForCard(entry.card)}
            params={DEFAULT_FOIL_PARAMS}
            onCover={setCovered}
          />
        ) : null}

        {selected ? (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              className="hand-fan-badge flex items-center justify-center rounded-full"
              style={{
                width: badgeSize,
                height: badgeSize,
                color: BAD,
                background: 'rgba(12,10,8,0.82)',
                border: `1px solid ${BAD}99`,
                boxShadow: `0 0 12px ${BAD}44, 0 2px 6px rgba(0,0,0,0.6)`,
              }}
            >
              <svg
                width={Math.round(badgeSize * 0.55)}
                height={Math.round(badgeSize * 0.55)}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
            </div>
          </div>
        ) : null}
      </div>
    </button>
  );
}

export default function HandFan({
  cards,
  selectedKeys,
  onToggle,
  onHover,
  keyFor,
  height,
}) {
  const N = cards.length;
  // Track canvas scale so the fan's pixel-driven layout math (sin/cos
  // arc transforms, card width/height) stays canvas-relative. Cqw can't
  // be used directly because the arcRadius/x/y math needs numbers.
  const [canvasScale, setCanvasScale] = useState(getCanvasScale());
  useEffect(() => onCanvasScaleChange(setCanvasScale), []);

  if (N === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-md"
        style={{
          height: height || Math.round(260 * canvasScale),
          background: 'rgba(0,0,0,0.25)',
          border: '1px dashed rgba(166,160,155,0.15)',
          color: 'rgba(166, 160, 155, 0.5)',
          fontSize: '0.75cqw',
        }}
      >
        {t('No cards to draw.')}
      </div>
    );
  }

  // Default design-pixel widths per hand size. Multiplied by canvasScale
  // so the fan grows in lockstep with the rest of the cqw-driven UI.
  const baseCardWidth = N <= 3 ? 200
    : N <= 4 ? 180
    : N <= 5 ? 170
    : N <= 6 ? 160
    : N <= 8 ? 148
    : 134;

  const siteFlags = cards.map(isSiteEntry);
  // Horizontal footprint per card, in units of cardWidth. A site is
  // rendered rotated 90deg about its bottom-centre, so it lies landscape:
  // it occupies cardHeight horizontally and starts AT its pivot rather
  // than straddling it. That 1.46x difference is why a uniform angular
  // step. A uniform *pixel* step. Left gaps around the spell/site
  // boundary. Advance is per-card instead, so neighbours always touch.
  const units = siteFlags.map((site) => (site ? CARD_ASPECT : 1));
  const leadUnits = units.slice(0, -1).reduce((sum, u) => sum + u, 0);
  const lastUnits = units[N - 1];

  // Each card reveals `overlap` of its own footprint before the next one
  // covers it. Proportional rather than a fixed step, so a landscape site
  // and a portrait spell both keep a sensible sliver showing.
  let overlap = N <= 4 ? 0.66 : N <= 6 ? 0.58 : 0.48;
  let cardWidth = Math.round(baseCardWidth * canvasScale);
  const maxSpan = MAX_FAN_SPAN * canvasScale;
  // Tighten the overlap before shrinking the cards; only an opener too
  // wide even at maximum overlap falls back to smaller cards. Avatars
  // that draw more or fewer than the standard 3+3 land here.
  if (leadUnits > 0 && (leadUnits * overlap + lastUnits) * cardWidth > maxSpan) {
    overlap = Math.max(MIN_OVERLAP, (maxSpan / cardWidth - lastUnits) / leadUnits);
  }
  const spanUnits = leadUnits * overlap + lastUnits;
  if (spanUnits * cardWidth > maxSpan) cardWidth = Math.floor(maxSpan / spanUnits);
  const cardHeight = Math.round(cardWidth * CARD_ASPECT);

  // Lay the hand out left to right by cumulative advance. `left` is the
  // card's visual left edge; the pivot the transform positions is the
  // element's bottom-centre, which sits half a card in for spells and
  // exactly on the left edge for rotated sites.
  const slots = [];
  let cursor = 0;
  for (let i = 0; i < N; i += 1) {
    const extent = siteFlags[i] ? cardHeight : cardWidth;
    slots.push({ site: siteFlags[i], left: cursor, extent });
    cursor += extent * overlap;
  }
  const span = slots[N - 1].left + slots[N - 1].extent;
  const spanCenter = span / 2;

  // Normalise the arc so the outermost cards always land at t = ±1. The
  // fan then keeps the same silhouette whether the avatar deals 4 cards
  // or 10, instead of flattening out as cards are added.
  let halfSpread = 0;
  for (const slot of slots) {
    halfSpread = Math.max(halfSpread, Math.abs(slot.left + slot.extent / 2 - spanCenter));
  }
  if (halfSpread === 0) halfSpread = 1;

  const arcRise = cardWidth * 0.13;
  const containerWidth = Math.round(span + 16);
  const containerHeight = height || Math.round(cardHeight + arcRise + 24);

  // Bottom-align rotated site cards with the unrotated spell cards.
  // Rotating a portrait card 90° around its bottom-center leaves the
  // rotated card centered vertically on the pivot. So half the card
  // (cardWidth/2) drops below the pivot. Lifting sites by cardWidth/2
  // pulls their visual bottom up to the same baseline as the spells,
  // producing a single coherent fan instead of a two-tier hand.
  //
  // Deliberately different from the in-game GameBoard hand (which uses
  // a smaller fixed offset tuned to the game board's scaled card size).
  // The dashboard widget has its own requirements. A clean baseline
  // read. So it owns its own correction here.
  const siteLift = cardWidth / 2;
  const badgeSize = Math.max(22, Math.round(cardWidth * 0.26));

  return (
    <div
      className="relative"
      style={{ height: containerHeight, width: containerWidth }}
    >
      <style>{FAN_CSS}</style>
      {cards.map((entry, i) => {
        const baseKey = keyFor ? keyFor(entry, i) : i;
        // Per-slot generation from the entry. Bumping an entry's gen
        // remounts just that card (replaying the draw-in animation)
        // while kept cards keep their key and stay put.
        const entryGen = entry?._entryGen ?? 0;
        const key = `${entryGen}:${baseKey}`;
        const selected = selectedKeys?.has?.(baseKey);
        const slot = slots[i];
        // Position along the hand, -1 (leftmost) .. +1 (rightmost).
        const t = (slot.left + slot.extent / 2 - spanCenter) / halfSpread;
        const angleDeg = t * HALF_ARC_DEG;
        // Every card pivots about a shared bottom-centre, so the tilted
        // outer cards ride higher than the middle ones. The cupped shape
        // of a hand held in one fist. Parabolic in t, which matches the
        // sagitta of the true arc closely at this shallow a tilt.
        const y = arcRise * t * t;
        const rotation = angleDeg + (slot.site ? 90 : 0);
        const pivotX = slot.left + (slot.site ? 0 : cardWidth / 2) - spanCenter;
        const liftedY = y + (slot.site ? siteLift : 0);
        const zIndex = selected ? N + 10 : i + 1;
        const imgUrl = cardImage(entry);

        const fanTx = `calc(-50% + ${Math.round(pivotX)}px)`;
        const fanTy = `${-Math.round(liftedY)}px`;
        const fanRot = `${rotation.toFixed(2)}deg`;
        // Stagger the draw-in: sites are listed after spells, so this
        // naturally deals left-to-right through the hand.
        const animationDelay = `${Math.min(i, 7) * 55}ms`;

        return (
          <FanCard
            key={key}
            entry={entry}
            imgUrl={imgUrl}
            isFoil={isFoilFinish(entry?.printing?.foiling)}
            foilTilt={foilTiltFor(t)}
            selected={selected}
            badgeSize={badgeSize}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            fanTx={fanTx}
            fanTy={fanTy}
            fanRot={fanRot}
            zIndex={zIndex}
            animationDelay={animationDelay}
            onClick={() => onToggle?.(baseKey, entry, i)}
            onMouseEnter={() => onHover?.(entry)}
            onMouseLeave={() => onHover?.(null)}
          />
        );
      })}
    </div>
  );
}
